/**
 * CreateCommunityModal — name/description/category/type/visibility/rules
 * form, then a preview step before creating.
 */

import React, { useState } from 'react';
import { BookOpen, PenTool, Globe, Lock, Mail, ArrowLeft, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Button, cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useCommunities } from './context/CommunitiesContext';
import { VISIBILITY_META } from './constants';
import { CATEGORIES, DEFAULT_RULES } from './constants/seedData';

const TONES = [
  { key: 'coral', className: 'bg-coral' },
  { key: 'yellow', className: 'bg-yellow' },
  { key: 'sage', className: 'bg-sage' },
  { key: 'blue', className: 'bg-blue' },
];

const TYPES = [
  { key: 'reader', label: 'Reader Community', blurb: 'Discussions, book clubs, reading challenges', icon: BookOpen },
  { key: 'writer', label: 'Writer Community', blurb: 'Prompts, critiques, sharing your work', icon: PenTool },
];

const VISIBILITY_ICON = { public: Globe, private: Lock, restricted: Mail };

const spineTone = {
  coral: 'from-coral to-[#b85e58]', yellow: 'from-yellow to-[#e0b164]', sage: 'from-sage to-[#a9c39d]', blue: 'from-blue to-[#6b808b]',
};

function Field({ label, children }) {
  return (
    <div className="mt-4">
      <label className="text-xs font-semibold uppercase tracking-wide text-subtle">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function FormStep({ data, setData, error, onNext }) {
  return (
    <div>
      <h2 className="font-display text-xl font-semibold text-ink">Create a Community</h2>
      {error && <p className="mt-3 rounded-lg bg-coral/10 px-3 py-2 text-xs font-medium text-coral">{error}</p>}

      <Field label="Community Name *">
        <Input value={data.name} onChange={e => setData(d => ({ ...d, name: e.target.value }))} placeholder="e.g., Comfort Reads Club" className="rounded-xl border-line bg-cream" />
      </Field>

      <Field label="Description">
        <Textarea value={data.description} onChange={e => setData(d => ({ ...d, description: e.target.value }))} placeholder="What is this community about?" className="rounded-xl border-line bg-cream text-sm" />
      </Field>

      <Field label="Tagline">
        <Input value={data.tagline} onChange={e => setData(d => ({ ...d, tagline: e.target.value }))} placeholder="A short description for discovery" className="rounded-xl border-line bg-cream" />
      </Field>

      <Field label="Category">
        <Select value={data.category} onValueChange={v => setData(d => ({ ...d, category: v }))}>
          <SelectTrigger className="rounded-xl border-line bg-cream"><SelectValue placeholder="Choose a category" /></SelectTrigger>
          <SelectContent>
            {CATEGORIES.filter(c => c !== 'All').map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>

      <Field label="Focus">
        <div className="grid grid-cols-2 gap-2.5">
          {TYPES.map(({ key, label, blurb, icon: Icon }) => (
            <button
              key={key} type="button" onClick={() => setData(d => ({ ...d, type: key }))}
              className={cx('rounded-xl border p-3 text-left transition-colors', data.type === key ? 'border-coral bg-coral/10' : 'border-line hover:border-coral/40')}
            >
              <Icon className={cx('h-4 w-4', data.type === key ? 'text-coral' : 'text-subtle')} />
              <p className="mt-1.5 text-sm font-semibold text-ink">{label}</p>
              <p className="text-[11px] text-subtle">{blurb}</p>
            </button>
          ))}
        </div>
      </Field>

      <Field label="Who can join">
        <div className="flex flex-wrap gap-2">
          {Object.entries(VISIBILITY_META).map(([key, meta]) => {
            const Icon = VISIBILITY_ICON[key];
            return (
              <button
                key={key} type="button" onClick={() => setData(d => ({ ...d, visibility: key }))}
                className={cx('flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors', data.visibility === key ? 'border-coral bg-coral/10 text-coral' : 'border-line text-ink hover:border-coral/40')}
                title={meta.hint}
              >
                <Icon className="h-3.5 w-3.5" /> {meta.label}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Accent">
        <div className="flex gap-2.5">
          {TONES.map(t => (
            <button key={t.key} type="button" onClick={() => setData(d => ({ ...d, tone: t.key }))} aria-label={`${t.key} accent`}
              className={cx('h-7 w-7 rounded-full ring-offset-2 ring-offset-card transition-all', t.className, data.tone === t.key ? 'ring-2 ring-ink' : 'opacity-70 hover:opacity-100')} />
          ))}
        </div>
      </Field>

      <Field label="Community Guidelines">
        <Textarea
          value={data.rulesText}
          onChange={e => setData(d => ({ ...d, rulesText: e.target.value }))}
          className="rounded-xl border-line bg-cream text-sm"
          rows={5}
        />
        <p className="mt-1 text-[11px] text-subtle">One rule per line. Moderators can edit these later.</p>
      </Field>

      <div className="mt-6 flex justify-end">
        <Button variant="primary" icon={false} onClick={onNext}>Preview</Button>
      </div>
    </div>
  );
}

function PreviewStep({ data, onBack, onConfirm }) {
  const tone = data.tone;
  return (
    <div>
      <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-xs font-medium text-subtle hover:text-coral">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to edit
      </button>

      <h2 className="mt-3 font-display text-xl font-semibold text-ink">Looks good?</h2>

      <div className="mt-4 rounded-2xl border border-line bg-card p-5 shadow-soft">
        <div className="flex items-start justify-between">
          <div className="flex -space-x-2" aria-hidden="true">
            {[0, 1, 2].map(i => <span key={i} className={cx('h-9 w-7 rounded-[3px] bg-gradient-to-br shadow-sm ring-2 ring-card', spineTone[tone])} />)}
          </div>
        </div>
        <h3 className="mt-4 font-display text-xl font-semibold text-ink">{data.name || 'Untitled Community'}</h3>
        <p className="mt-2 text-sm text-subtle">{data.description || 'No description yet.'}</p>
        <div className="mt-4 flex items-center gap-1.5 text-xs text-subtle">
          <Users className="h-3.5 w-3.5" /> 1 member — you're the first
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-2.5">
        <Button variant="secondary" icon={false} onClick={onBack}>Back</Button>
        <Button variant="primary" icon={false} onClick={onConfirm}>Create Community</Button>
      </div>
    </div>
  );
}

function ModalBody({ onClose, onCreated }) {
  const { createCommunity } = useCommunities();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [data, setData] = useState({
    name: '', tagline: '', description: '', category: 'Genres', type: 'reader', visibility: 'public',
    tone: 'coral', rulesText: DEFAULT_RULES.join('\n'),
  });

  const handleNext = () => {
    if (!data.name.trim()) return setError('Community name is required');
    if (!data.category) return setError('Choose a category');
    setError('');
    setStep(2);
  };

  const handleConfirm = async () => {
    try {
      const community = await createCommunity({
        name: data.name.trim(),
        type: data.type,
        visibility: data.visibility,
        tagline: data.tagline.trim(),
        description: data.description.trim(),
        category: data.category,
        tone: data.tone,
        rules: data.rulesText.split('\n').map(r => r.trim()).filter(Boolean),
      });
      toast(`Created “${community.name}” ✨`);
      onCreated?.(community);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create community');
      setStep(1);
    }
  };

  return step === 1
    ? <FormStep data={data} setData={setData} error={error} onNext={handleNext} />
    : <PreviewStep data={data} onBack={() => setStep(1)} onConfirm={handleConfirm} />;
}

export default function CreateCommunityModal({ isOpen, onClose, onCreated }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-h-[88vh] w-[92vw] max-w-lg overflow-y-auto rounded-2xl border-line bg-card p-6 text-ink">
          <DialogTitle className="sr-only">Create a Community</DialogTitle>
          <ModalBody onClose={onClose} onCreated={onCreated} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="max-h-[92vh] overflow-y-auto border-line bg-card p-5 pb-8 text-ink">
        <DrawerTitle className="sr-only">Create a Community</DrawerTitle>
        <ModalBody onClose={onClose} onCreated={onCreated} />
      </DrawerContent>
    </Drawer>
  );
}
